/**
 * Top-level data-router (createBrowserRouter — react-router-dom v7 LIBRARY mode).
 *
 * Public auth routes (`auth/return`, `auth/error`, `auth/signed-out`) render
 * outside the AuthGate. Everything else sits under the AuthGate boot guard + the
 * AppLayout shell.
 *
 * Authenticated area: Users / Sales / Attendance / Products are warehouse
 * roster tables; Sync Reports is the tenant jobs/journal page; Configuration
 * is product-family mapping + membership; Team & access is login staff;
 * Membership Stats is the live report at `/reports/membership` (+ `/:month`).
 * Revenue Stats is the live report at `/reports/revenue` (+ family / lines).
 * Dashboard (index) is the live series charts page (WSRADM-0037); Engine stays
 * Coming Soon. `/reports` redirects to `/reports/membership`. Catch-all 404.
 * No `/dashboards` second path.
 */
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { RootLayout } from '@/routes/RootLayout';
import { RouteError } from '@/routes/RouteError';
import { AuthReturn } from '@/routes/AuthReturn';
import { AuthError } from '@/routes/AuthError';
import { SignedOut } from '@/routes/SignedOut';
import { NotFound } from '@/routes/NotFound';
import { ComingSoonPage } from '@/routes/ComingSoonPage';
import { ConfigurationPage } from '@/routes/configuration/ConfigurationPage';
import { SyncReportsPage } from '@/routes/sync-reports/SyncReportsPage';
import { UsersPage } from '@/routes/users/UsersPage';
import { MemberPage } from '@/routes/users/MemberPage';
import { SalesPage } from '@/routes/sales/SalesPage';
import { AttendancePage } from '@/routes/attendance/AttendancePage';
import { ProductPage } from '@/routes/products/ProductPage';
import { ProductsPage } from '@/routes/products/ProductsPage';
import { TeamPage } from '@/routes/team/TeamPage';
import { MembershipStatsPage } from '@/routes/MembershipStatsPage';
import { MembershipMonthPage } from '@/routes/MembershipMonthPage';
import { RevenueStatsPage } from '@/routes/RevenueStatsPage';
import { RevenueFamilyPage } from '@/routes/RevenueFamilyPage';
import { RevenueLinesPage } from '@/routes/RevenueLinesPage';
import { AuthGate } from '@/auth/AuthGate';
import { AppLayout } from '@/components/AppLayout';

const DashboardPage = lazy(() =>
  import('@/routes/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      // Public auth routes (no AuthGate).
      { path: 'auth/return', element: <AuthReturn /> },
      { path: 'auth/error', element: <AuthError /> },
      { path: 'auth/signed-out', element: <SignedOut /> },

      // Everything else is gated by auth (boot guard) + the AppLayout shell.
      {
        element: <AuthGate />,
        children: [
          {
            element: <AppLayout />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={null}>
                    <DashboardPage />
                  </Suspense>
                ),
              },
              {
                path: 'reports',
                children: [
                  { index: true, element: <Navigate to="/reports/membership" replace /> },
                  { path: 'membership', element: <MembershipStatsPage /> },
                  { path: 'membership/:month', element: <MembershipMonthPage /> },
                  { path: 'revenue', element: <RevenueStatsPage /> },
                  { path: 'revenue/:month/:familySlug/lines', element: <RevenueLinesPage /> },
                  { path: 'revenue/:month/:familySlug', element: <RevenueFamilyPage /> },
                ],
              },
              { path: 'users', element: <UsersPage /> },
              { path: 'users/:sourceUserId', element: <MemberPage /> },
              { path: 'sales', element: <SalesPage /> },
              { path: 'attendance', element: <AttendancePage /> },
              { path: 'products', element: <ProductsPage /> },
              { path: 'products/:sourceProductId', element: <ProductPage /> },
              {
                path: 'sync-reports',
                element: <SyncReportsPage />,
              },
              { path: 'configuration', element: <ConfigurationPage /> },
              { path: 'team', element: <TeamPage /> },
              {
                path: 'engine',
                element: <ComingSoonPage titleKey="page.engine.title" icon="memory" fallback="Engine" />,
              },

              { path: '*', element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
